import { createContext, useContext, useState, useEffect, useRef } from 'react';
import apiClient from '../lib/apiClient';
import { initSocketClient } from '../lib/socketClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  
  const [userPvzs, setUserPvzs] = useState([]);
  const [activePvzState, setActivePvzState] = useState(null);
  const [selectedPvzIds, setSelectedPvzIdsState] = useState(() => {
    try {
      const saved = localStorage.getItem('wb_selected_pvz_ids');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  
  const socketRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const presenceStatusRef = useRef('online');

  const setSelectedPvzIds = (ids) => {
    setSelectedPvzIdsState(ids);
    try {
      localStorage.setItem('wb_selected_pvz_ids', JSON.stringify(ids));
    } catch (e) {
      console.error(e);
    }
  };

  const setActivePvz = (pvz) => {
    setActivePvzState(pvz);
    if (pvz?.id) {
      localStorage.setItem('wb_active_pvz_id', pvz.id);
    } else {
      localStorage.removeItem('wb_active_pvz_id');
    }
  };
  const activePvz = activePvzState;

  const fetchProfile = async () => {
    try {
      const profileData = await apiClient('/auth/me');
      if (profileData && profileData.id) {
        setUser({ id: profileData.id, email: profileData.email });
        setProfile(profileData);
        // Загружаем реальный список ПВЗ, к которым у пользователя есть доступ
        const pvzList = profileData.pvzs || [];
        setUserPvzs(pvzList);
        
        const savedPvzId = localStorage.getItem('wb_active_pvz_id');
        const matchedPvz = pvzList.find(p => p.id === savedPvzId) || pvzList.find(p => p.is_primary) || pvzList[0] || null;
        setActivePvz(matchedPvz);

        try {
          const savedIds = localStorage.getItem('wb_selected_pvz_ids');
          const parsedIds = savedIds ? JSON.parse(savedIds) : [];
          const validIds = parsedIds.filter(id => pvzList.some(p => p.id === id));
          if (validIds.length > 0) {
            setSelectedPvzIds(validIds);
          } else if (matchedPvz?.id) {
            setSelectedPvzIds([matchedPvz.id]);
          }
        } catch (e) {
          if (matchedPvz?.id) setSelectedPvzIds([matchedPvz.id]);
        }

        // Инициализация WebSocket
        const token = localStorage.getItem('token');
        if (token) {
          // Отключаем старый сокет перед созданием нового
          if (socketRef.current) {
            socketRef.current.off('profile-updated');
            socketRef.current.disconnect();
            socketRef.current = null;
          }
          const s = initSocketClient(token);
          socketRef.current = s;
          setSocket(s);
          presenceStatusRef.current = 'online';
          
          s.on('profile-updated', async (updatedData) => {
            console.log('[WS] Profile updated:', updatedData);
            // НЕ вызываем fetchProfile() рекурсивно — 
            // просто получаем свежий профиль и обновляем стейт без пересоздания сокета
            try {
              const freshProfile = await apiClient('/auth/me');
              if (freshProfile && freshProfile.id) {
                setUser({ id: freshProfile.id, email: freshProfile.email });
                setProfile(freshProfile);
                const pvzList = freshProfile.pvzs || [];
                setUserPvzs(pvzList);
              }
            } catch (err) {
              console.error('[WS] Failed to refresh profile:', err);
            }
          });
        }

        return profileData;
      } else {
        setUser(null);
        setProfile(null);
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setSocket(null);
      }
    } catch (err) {
      console.error('Ошибка загрузки профиля:', err);
      setUser(null);
      setProfile(null);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
    
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const clearAllAppCaches = () => {
    try {
      Object.keys(localStorage).forEach(key => {
        if (
          key.startsWith('cache_payments_') ||
          key.startsWith('cache_analytics_') ||
          key.startsWith('wb_cached_') ||
          key.startsWith('chat_active_')
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {}
  };

  const signIn = async (identifier, password) => {
    try {
      clearAllAppCaches();
      localStorage.removeItem('wb_active_pvz_id');
      localStorage.removeItem('wb_selected_pvz_ids');
      const data = await apiClient('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: identifier, password })
      });
      localStorage.setItem('token', data.token);
      await fetchProfile();
      return data;
    } catch (err) {
      throw new Error(err.message || 'Ошибка входа');
    }
  };

  const signOut = async () => {
    try {
      const { unsubscribeFromPushNotifications } = await import('../lib/notifications');
      await unsubscribeFromPushNotifications();
    } catch (e) {
      console.warn('Push unsubscribe failed:', e);
    }
    clearAllAppCaches();
    localStorage.removeItem('token');
    localStorage.removeItem('wb_active_pvz_id');
    localStorage.removeItem('wb_selected_pvz_ids');
    sessionStorage.removeItem('superadmin_token');
    sessionStorage.removeItem('pin_unlocked');
    setUser(null);
    setProfile(null);
    setUserPvzs([]);
    setActivePvz(null);
    setSelectedPvzIds([]);
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSocket(null);
  };

  const signUp = async (email, password, fullName, inviteCode, login) => {
    clearAllAppCaches();
    localStorage.removeItem('wb_active_pvz_id');
    localStorage.removeItem('wb_selected_pvz_ids');
    const data = await apiClient('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName, invite_code: inviteCode, login })
    });
    localStorage.setItem('token', data.token);
    await fetchProfile();
    return data;
  };

  const signUpOwner = async (email, password, ownerData) => {
    clearAllAppCaches();
    localStorage.removeItem('wb_active_pvz_id');
    localStorage.removeItem('wb_selected_pvz_ids');
    const data = await apiClient('/auth/register-owner', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...ownerData })
    });
    localStorage.setItem('token', data.token);
    await fetchProfile();
    return data;
  };

  const demoLogin = () => { setIsDemoMode(true); };
  
  const resetPassword = async (email) => {
    return await apiClient('/auth/reset-password/request', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  };
  const updatePassword = async (token, newPassword) => {
    return await apiClient('/auth/reset-password/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
  };
  const checkResetMethods = async (identifier) => {
    return await apiClient('/auth/reset-password/check', {
      method: 'POST',
      body: JSON.stringify({ identifier })
    });
  };
  const verifyReset = async (userId, method, data) => {
    return await apiClient('/auth/reset-password/verify', {
      method: 'POST',
      body: JSON.stringify({ userId, method, ...data })
    });
  };

  const isAdmin = profile?.role === 'admin' || profile?.role === 'owner';
  const isOwner = profile?.role === 'owner';

  const rolePermissions = {
    admin: profile?.org?.admin_permissions || {},
    employee: profile?.org?.employee_permissions || {},
    helper: profile?.org?.helper_permissions || {}
  };
  const currentRole = profile?.role || 'employee';
  const perms = rolePermissions[currentRole] || {};

  // Глобальный трекер физической активности для статуса присутствия (уход за 10 минут)
  useEffect(() => {
    if (!socket) return;

    const handleActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (presenceStatusRef.current === 'away') {
        presenceStatusRef.current = 'online';
        socket.emit('user-online');
        console.log('[Presence] Пользователь вернулся в сеть');
      }
    };

    // Слушаем физическую активность пользователя
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Таймер проверки на бездействие (каждые 15 секунд)
    const idleInterval = setInterval(() => {
      const idleTime = Date.now() - lastActivityRef.current;
      const TEN_MINUTES = 10 * 60 * 1000; // 10 минут без активности

      if (idleTime >= TEN_MINUTES && presenceStatusRef.current === 'online') {
        presenceStatusRef.current = 'away';
        socket.emit('user-away');
        console.log('[Presence] Пользователь отошел (10 минут бездействия)');
      }
    }, 15000);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      clearInterval(idleInterval);
    };
  }, [socket]);

  const value = {
    user,
    profile,
    setProfile,
    loading,
    isDemoMode,
    isAdmin,
    isOwner,
    isAdminEmployee: false,
    adminPermissions: perms,
    rolePermissions,
    userPvzs,
    activePvz,
    setActivePvz,
    selectedPvzIds,
    setSelectedPvzIds,
    signUp,
    signUpOwner,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    checkResetMethods,
    verifyReset,
    demoLogin,
    fetchProfile,
    socket, // Предоставляем доступ к сокету глобально
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
